import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Typography } from "@mui/material";
import {
  TOP_64_RULES_PATH,
  TOP_64_SCORING_TIERS,
  summarizeTop64ScoreBreakdown,
  formatTop64TierCount,
} from "../../constants/top64Competition";
import "./Top64CompetitionScore.css";

function playerName(row) {
  return [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "Unknown";
}

function Top64CompetitionScore({ competitionScore, compact = false }) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const missedPicks = useMemo(
    () =>
      (competitionScore?.breakdown || []).filter((row) => row.predicted_rank == null),
    [competitionScore]
  );

  const tierSummary = useMemo(
    () => summarizeTop64ScoreBreakdown(competitionScore?.breakdown || []),
    [competitionScore]
  );

  if (!competitionScore?.draft_complete) {
    return null;
  }

  const { total_score: totalScore, max_score: maxScore, breakdown = [] } = competitionScore;

  return (
    <section
      className={[
        "top64-competition-score",
        compact ? "top64-competition-score--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Competition score"
    >
      <div className="top64-competition-score-summary">
        <div className="top64-competition-score-heading">
          <span className="top64-competition-score-eyebrow">Top 64 competition</span>
          <Typography component="h2" className="top64-competition-score-total">
            {totalScore.toLocaleString()}
            <span className="top64-competition-score-total-max">
              {" "}
              / {maxScore.toLocaleString()} pts
            </span>
          </Typography>
        </div>
        {!compact && (
          <p className="top64-competition-score-lead">
            Scored from the first 64 picks using the{" "}
            <Link to={TOP_64_RULES_PATH} className="link-default">
              competition rubric
            </Link>
            .
          </p>
        )}
      </div>

      {!compact && (
        <>
          {tierSummary.length > 0 && (
            <div className="top64-competition-score-tier-summary" aria-label="Score breakdown by tier">
              <p className="top64-competition-score-tier-summary-label">By tier</p>
              <ul className="top64-competition-score-tier-summary-list">
                {tierSummary.map((tier) => (
                  <li key={tier.points} className="top64-competition-score-tier-summary-item">
                    <span className="top64-competition-score-tier-summary-count">
                      {tier.count}
                    </span>
                    <span className="top64-competition-score-tier-summary-phrase">
                      {formatTop64TierCount(tier)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="top64-competition-score-rubric">
            {TOP_64_SCORING_TIERS.map((tier) => (
              <span key={tier.label} className="top64-competition-score-rubric-chip">
                {tier.label}: {tier.points}
              </span>
            ))}
            <span className="top64-competition-score-rubric-chip top64-competition-score-rubric-chip--muted">
              Not on board: 0
            </span>
          </div>

          <button
            type="button"
            className="top64-competition-score-toggle"
            onClick={() => setBreakdownOpen((open) => !open)}
            aria-expanded={breakdownOpen}
          >
            {breakdownOpen ? "Hide pick breakdown" : "Show pick breakdown"}
          </button>

          {breakdownOpen && (
            <div className="top64-competition-score-breakdown">
              <table className="top64-competition-score-table">
                <thead>
                  <tr>
                    <th scope="col">Pick</th>
                    <th scope="col">Player</th>
                    <th scope="col">Your rank</th>
                    <th scope="col">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row) => (
                    <tr
                      key={`${row.player_id}-${row.overall_pick}`}
                      className={
                        row.predicted_rank == null
                          ? "top64-competition-score-row--missed"
                          : ""
                      }
                    >
                      <td>#{row.overall_pick}</td>
                      <td>
                        <Link
                          to={`/player/${row.player_id}`}
                          className="link-default top64-competition-score-player"
                        >
                          {playerName(row)}
                        </Link>
                      </td>
                      <td>
                        {row.predicted_rank != null ? `#${row.predicted_rank}` : "—"}
                      </td>
                      <td className="top64-competition-score-points">{row.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {missedPicks.length > 0 && (
                <p className="top64-competition-score-missed-note">
                  {missedPicks.length} drafted player
                  {missedPicks.length === 1 ? "" : "s"} not on this board (0 pts each).
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default Top64CompetitionScore;
